import React, { useEffect, useMemo, useState } from 'react';
import styled from 'astroturf/react';

import {
  recordShow,
  recordDismissal,
  recordClickthrough,
} from 'js/utils/dismissibles';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';

import { Body } from './Body';
import { Button } from './Button';
import { Heading } from './Heading';
import { Icon } from './Icon';
import _DefaultContainer from "Unibrow-DefaultContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=DefaultContainer";
import _AlertContainer from "Unibrow-AlertContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=AlertContainer";
import _BeigeContainer from "Unibrow-BeigeContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=BeigeContainer";
import _BannerContainer from "Unibrow-BannerContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=BannerContainer";
import _TextContainer from "Unibrow-TextContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=TextContainer";
import _IconContainer from "Unibrow-IconContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=IconContainer";
import _TextBlock from "Unibrow-TextBlock.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=TextBlock";
import _Eyebrow from "Unibrow-Eyebrow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=Eyebrow";
import _ContentContainer from "Unibrow-ContentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=ContentContainer";
import _ButtonContainer from "Unibrow-ButtonContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=ButtonContainer";
import _ActionButton from "Unibrow-ActionButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=ActionButton";
import _CloseButton from "Unibrow-CloseButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Unibrow.tsx?styleId=CloseButton";


const DefaultContainer = /*#__PURE__*/styled("div", null, {
  displayName: "DefaultContainer",
  styles: _DefaultContainer
});

const AlertContainer = /*#__PURE__*/styled(DefaultContainer, null, {
  displayName: "AlertContainer",
  styles: _AlertContainer
});

const BeigeContainer = /*#__PURE__*/styled(DefaultContainer, null, {
  displayName: "BeigeContainer",
  styles: _BeigeContainer
});

const BannerContainer = /*#__PURE__*/styled("div", null, {
  displayName: "BannerContainer",
  styles: _BannerContainer
});

const TextContainer = /*#__PURE__*/styled("div", null, {
  displayName: "TextContainer",
  styles: _TextContainer
});

const IconContainer = /*#__PURE__*/styled("div", null, {
  displayName: "IconContainer",
  styles: _IconContainer
});

const TextBlock = /*#__PURE__*/styled("div", null, {
  displayName: "TextBlock",
  styles: _TextBlock
});

const Eyebrow = /*#__PURE__*/styled("div", null, {
  displayName: "Eyebrow",
  styles: _Eyebrow
});

const ContentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentContainer",
  styles: _ContentContainer
});

const ButtonContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ButtonContainer",
  styles: _ButtonContainer
});

const ActionButton = /*#__PURE__*/styled(Button, null, {
  displayName: "ActionButton",
  styles: _ActionButton
});

const CloseButton = /*#__PURE__*/styled("div", null, {
  displayName: "CloseButton",
  styles: _CloseButton
});

type UnibrowVariant = 'default' | 'alert' | 'beige';

type UnibrowButton = {
  text: string;
  onClick: () => void;
  icon?: string | React.ReactNode;
  iconRight?: string | React.ReactNode;
  trackClick?: TrackClickOptions;
};

export type UnibrowProps = {
  dismissibleName: string;
  variant?: UnibrowVariant;
  icon?: string | React.ReactNode;
  text: string;
  content?: React.ReactNode;
  eyebrowIcon?: string | React.ReactNode;
  eyebrowText?: string;
  primaryButton?: UnibrowButton;
  secondaryButton?: UnibrowButton;
};

/**
 * A full-width banner that appears at the top of the page above the navbar, for uses
 * such as alerts, reminders, announcements, upsells, etc. Automatically tracked displays
 * and clicks to the `dismissibles` table, using the given `dismissibleName`.
 *
 * Props:
 * - `dismissibleName`: Unique identifier for tracking dismissal state (required)
 * - `variant`: Visual style variant - 'default' (dark), 'alert' (red), or 'beige'
 * - `icon`: The name of a Material Symbols icon (string) or an arbitrary React component to display
 *     as the banner's main icon (left of text)
 * - `text`: Main heading text for the banner (required)
 * - `content`: Optional React node to display as additional content in the center of the banner
 * - `eyebrowIcon`: The name of a Material Symbols icon (string) or React component for the eyebrow
 * - `eyebrowText`: Small text label displayed above the main heading
 * - `primaryButton`: Primary action button configuration with text, onClick handler, and optional icons and click tracking options
 * - `secondaryButton`: Secondary action button configuration with text, onClick handler, and optional icons and click tracking options
 *
 * ## Basic Usage
 *
 * ```tsx
 * <Unibrow
 *   dismissibleName="feature-announcement"
 *   text="New features available!"
 *   primaryButton={{
 *     text: "Learn More",
 *     onClick: () => window.location.href = "/features"
 *   }}
 * />
 * ```
 *
 * ## Eyebrow + Alert Variant
 *
 * ```tsx
 * <Unibrow
 *   dismissibleName="payment-alert"
 *   variant="alert"
 *   icon="warning"
 *   eyebrowText="Payment Problem"
 *   text="Your payment was declined — update now to keep your Premium access"
 *   primaryButton={{
 *     text: "Update Payment",
 *     onClick: () => window.location.href = "/settings/payment"
 *   }}
 * />
 * ```
 */
export default function Unibrow({
  dismissibleName,
  variant = 'default',
  icon,
  text,
  content,
  eyebrowIcon,
  eyebrowText,
  primaryButton,
  secondaryButton,
}: UnibrowProps) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    recordShow(dismissibleName);
  }, [dismissibleName]);

  const handleButtonClick = ({
    onClick,
    trackClick: trackClickOptions,
  }: UnibrowButton) => {
    recordClickthrough(dismissibleName);
    if (trackClickOptions) {
      trackClick(trackClickOptions);
    }
    onClick();
    setShow(false);
  };

  const handleClose = () => {
    recordDismissal(dismissibleName);
    setShow(false);
  };

  const Container = useMemo(() => {
    switch (variant) {
      case 'alert':
        return AlertContainer;
      case 'beige':
        return BeigeContainer;
      default:
        return DefaultContainer;
    }
  }, [variant]);

  if (!show) return null;

  return (
    <Container>
      <BannerContainer>
        {icon &&
          (typeof icon === 'string' ? (
            <IconContainer>
              <Icon name={icon} size={32} />
            </IconContainer>
          ) : (
            <IconContainer>{icon}</IconContainer>
          ))}
        <TextContainer>
          <TextBlock>
            {eyebrowText && (
              <Eyebrow>
                {eyebrowIcon && (
                  <IconContainer>
                    {typeof eyebrowIcon === 'string' ? (
                      <Icon name={eyebrowIcon} size={16} />
                    ) : (
                      eyebrowIcon
                    )}
                  </IconContainer>
                )}
                <Body size="sm" bold>
                  {eyebrowText}
                </Body>
              </Eyebrow>
            )}
            <Heading size="sm" fontFamily="sans-serif">
              {text}
            </Heading>
          </TextBlock>
        </TextContainer>
      </BannerContainer>
      {content && <ContentContainer>{content}</ContentContainer>}
      {(primaryButton || secondaryButton) && (
        <ButtonContainer>
          {primaryButton && (
            <ActionButton
              inverse={variant === 'default'}
              size="sm"
              icon={primaryButton.icon}
              iconRight={primaryButton.iconRight}
              onClick={() => handleButtonClick(primaryButton)}
            >
              {primaryButton.text}
            </ActionButton>
          )}
          {secondaryButton && (
            <ActionButton
              inverse={variant === 'default'}
              size="sm"
              secondary
              icon={secondaryButton.icon}
              iconRight={secondaryButton.iconRight}
              onClick={() => handleButtonClick(secondaryButton)}
            >
              {secondaryButton.text}
            </ActionButton>
          )}
        </ButtonContainer>
      )}
      <CloseButton
        onClick={handleClose}
        role="button"
        aria-label="Close"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClose();
          }
        }}
      >
        <Icon name="close" />
      </CloseButton>
    </Container>
  );
}
